import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Clock, ArrowLeft, ArrowUpRight, Calendar, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { FinalCTA } from "@/components/sections";
import { Reveal } from "@/components/motion";
import { ARTICLES } from "@/data/blog";

// Lightweight markdown renderer for article bodies (line-based).
function MarkdownLite({ text }) {
  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-navy font-semibold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');

  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length) {
      const buf = listBuf;
      out.push(
        <ul key={`ul-${key}`} className="my-6 space-y-3 pl-1">
          {buf.map((li, i) => (
            <li key={i} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
              <span dangerouslySetInnerHTML={{ __html: inline(li) }} />
            </li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };

  lines.forEach((raw, idx) => {
    const t = raw.trim();
    if (!t) { flushList(idx); return; }
    if (t.startsWith("### ")) {
      flushList(idx);
      out.push(<h3 key={idx} className="mt-10 mb-3 font-display text-2xl text-navy">{t.slice(4)}</h3>);
    } else if (t.startsWith("## ")) {
      flushList(idx);
      out.push(<h2 key={idx} className="mt-12 mb-4 font-display text-3xl text-navy tracking-tight">{t.slice(3)}</h2>);
    } else if (t.startsWith("- ")) {
      listBuf.push(t.slice(2));
    } else {
      flushList(idx);
      out.push(<p key={idx} className="my-5 text-lg leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{ __html: inline(t) }} />);
    }
  });
  flushList("end");
  return <div>{out}</div>;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  if (!article) return <Navigate to="/resources" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 3);
  const fallback = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const suggestions = related.length ? related : fallback;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Resources", path: "/resources" },
    { name: article.title, path: `/resources/${slug}` },
  ];

  const articleSchema = {
    "@context": "https://schema.org", "@type": "Article",
    headline: article.title, description: article.excerpt, image: article.image,
    datePublished: article.date, author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: "Beacon Counseling, LLC" },
  };

  return (
    <>
      <SEO title={article.title} description={article.excerpt} path={`/resources/${slug}`}
        image={article.image} type="article"
        jsonLd={[orgSchema, breadcrumbSchema(crumbs), articleSchema]} />

      {/* Reading progress */}
      <motion.div style={{ scaleX: progress }} className="fixed left-0 top-0 z-[60] h-1 w-full origin-left bg-teal" />

      {/* Header */}
      <article>
        <header className="relative overflow-hidden bg-navy pt-32 pb-16 lg:pt-40">
          <div className="pointer-events-none absolute inset-0 opacity-20">
            <img src={article.image} alt="" className="h-full w-full object-cover" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
          </div>
          <div className="relative mx-auto max-w-3xl px-6 lg:px-12">
            <Reveal>
              <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-teal-light transition-colors">
                <ArrowLeft className="h-4 w-4" /> All articles
              </Link>
            </Reveal>
            <Reveal delay={0.05}>
              <span className="mt-6 inline-block rounded-full bg-teal px-3 py-1 text-xs font-medium text-white">{article.category}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight text-white text-balance">{article.title}</h1>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
                <span className="flex items-center gap-2"><User className="h-4 w-4" />{article.author}</span>
                <span className="flex items-center gap-2"><Calendar className="h-4 w-4" />{new Date(article.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{article.readTime}</span>
              </div>
            </Reveal>
          </div>
        </header>

        {/* Cover image */}
        <div className="mx-auto -mt-8 max-w-4xl px-6 lg:px-12">
          <Reveal>
            <img src={article.image} alt={article.title} className="aspect-[16/9] w-full rounded-[2rem] object-cover shadow-premium" />
          </Reveal>
        </div>

        {/* Body */}
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-12">
          <p className="mb-8 font-display text-2xl leading-snug text-navy">{article.excerpt}</p>
          <MarkdownLite text={article.body} />

          <div className="mt-12 rounded-3xl bg-secondary p-8 text-center">
            <p className="font-display text-2xl text-navy">Want support putting this into practice?</p>
            <p className="mt-3 text-muted-foreground">At Beacon, David helps people apply these skills every day.</p>
            <Link to="/contact">
              <Button className="mt-6 rounded-full bg-teal hover:bg-teal-dark text-white h-12 px-8">Book a Consultation <ArrowUpRight className="ml-1 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <h2 className="font-display text-3xl text-navy sm:text-4xl">Keep reading</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {suggestions.map((a) => (
              <Link key={a.slug} to={`/resources/${a.slug}`} className="group flex flex-col overflow-hidden rounded-3xl border border-navy-100 bg-white shadow-soft transition-all hover:-translate-y-1 hover:shadow-premium">
                <img src={a.image} alt={a.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-medium text-teal">{a.category}</span>
                  <h3 className="mt-2 font-display text-lg text-navy group-hover:text-teal transition-colors">{a.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
